import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { User } from '@/@types/user';
import { AUTHENTICATE, BASE_URL } from '@/configs/endpoints';
import { registerReset, resetAllStores } from './resetStore';
import api from '@/services/api/axiosInstance';
import { logoutUser } from '@/services/userApi';

interface AuthState {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    isInitialized: boolean;
    errorMessage: string | null;
    isLoggingOut: boolean;
    sessionLocked: boolean;
    sessionLockMessage: string | null;
    userLoginInfo: number | null;    login: (credentials: { username: string; password: string; rememberMe: boolean; recaptchaToken?: string }) => Promise<void>;
    logout: () => Promise<void>;
    initialize: () => Promise<void>;
    setSocketSessionId: (socketSessionId: string) => void;
    setDomainCode: (domainCode: string) => void;
    setAgentStatus: (status: { id: string, value: string }) => void;
    setSessionLocked: (locked: boolean) => void;
    setSessionLockMessage: (message: string | null) => void;
    updateUserQueues: (queues: { id: number; name: string }[]) => void;
    socketSessionId: string | null;
    domainCode: string;
    agentStatus: { id: string, value: string } | null;
}

const initialState = {
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false,
    isInitialized: false,
    errorMessage: null,
    isLoggingOut: false,
    sessionLocked: false,
    sessionLockMessage: null,
    userLoginInfo: null,
    socketSessionId: null,
    domainCode: "",
    agentStatus: { id: "Available", value: "AVAILABLE" },
};

export const useAuthStore = create<AuthState>()(
    devtools(
        persist(
            (set) => ({
                ...initialState,
                login: async (credentials) => {
                    set({ isLoading: true, errorMessage: null, sessionLocked: false, sessionLockMessage: null });
                    try {
                        const response = await api.post(
                            `${BASE_URL}${AUTHENTICATE}`,
                            credentials
                        );
                        // Check for error in response
                        if (response && response?.data?.codeId !== 1200) {
                            throw new Error(response?.data?.message || 'Login failed');
                        }

                        console.log(response, "auth response");


                        const parsed = response.data.data;

                        console.log(parsed, "auth response");

                        const { authToken, userLoginInfo, ...userData } = parsed;

                        console.log(parsed, "auth response");

                        if (typeof authToken !== 'string') {
                            throw new Error('Invalid response');
                        }

                        // Save token to localStorage for initialize function

                        console.log(userData, "user data")

                        // Extract domainCode from domainCodes array
                        const domainCode = userData.domainCode || null;

                        // Build complete user object with all fields
                        const user: User = {
                            userId: userData.userId,
                            username: userData.username,
                            domainUrl: userData.domainUrl,
                            domainCode: userData.domainCode || null,
                            orgCode: userData.orgCode,
                            roleId: userData.roleId,
                            roleName: userData.roleName,
                            roleLevel: userData.roleLevel,
                            queueIdAndName: userData.queueIdAndName || [],
                            firstName: userData.firstName,
                            lastName: userData.lastName,
                            authority: userData.authority,
                            languageCodes: userData.languageCodes || [],
                            permissionNames: userData.permissionNames || [],
                        };

                        set({
                            user,
                            token: authToken,
                            domainCode,
                            userLoginInfo: typeof userLoginInfo === 'number' ? userLoginInfo : null,
                            isAuthenticated: true,
                            isLoading: false,
                            sessionLocked: false,
                            sessionLockMessage: null,
                        });
                    } catch (err: any) {
                        // Extract error detail from API response
                        const errorDetail = err?.response?.data?.detail || err?.response?.data?.message || err?.message || 'Login failed';
                        set({
                            errorMessage: errorDetail,
                            isLoading: false,
                        });
                    }
                },
                logout: async () => {
                    set({ isLoggingOut: true, sessionLocked: false, sessionLockMessage: null });
                    const userLoginInfo = useAuthStore.getState().userLoginInfo;
                    const sessionAvailable = !!(useAuthStore.getState().socketSessionId);
                    try {
                        if (userLoginInfo) {
                            await logoutUser(userLoginInfo, sessionAvailable);
                        }
                    } catch (err) {
                        console.error('Logout API failed', err);
                    }

                    // Clear all localStorage keys
                    window.localStorage.removeItem('authToken');
                    window.localStorage.removeItem('auth-storage');
                    window.localStorage.removeItem('chat-storage');
                    window.localStorage.removeItem('request-storage');
                    window.localStorage.clear();

                    resetAllStores();
                    set(initialState);
                    set({ isLoggingOut: false });
                    set({ isInitialized: true, sessionLocked: false, sessionLockMessage: null });
                },
                initialize: async () => {
                    try {
                        set({ isInitialized: true, sessionLocked: false, sessionLockMessage: null });
                    } catch (err) {
                        console.error(err);
                        set({ isInitialized: true, sessionLocked: false, sessionLockMessage: null });
                    }
                },
                setSocketSessionId: (socketSessionId: string) => set({ socketSessionId }),
                setDomainCode: (domainCode: string) => set({ domainCode }),
                setAgentStatus: (status: { id: string, value: string }) => set({ agentStatus: status }),
                setSessionLocked: (locked: boolean) => set({ sessionLocked: locked }),
                setSessionLockMessage: (message: string | null) => set({ sessionLockMessage: message }),
                updateUserQueues: (newQueues: { id: number; name: string }[]) =>
                    set((state) => {
                        if (!state.user) return state;
                        const currentQueues = state.user.queueIdAndName || [];
                        const isDifferent =
                            currentQueues.length !== newQueues.length ||
                            newQueues.some((nq, index) => {
                                const cq = currentQueues[index];
                                return !cq || Number(cq.id) !== Number(nq.id) || cq.name !== nq.name;
                            });

                        if (isDifferent) {
                            return {
                                user: {
                                    ...state.user,
                                    queueIdAndName: newQueues,
                                },
                            };
                        }
                        return state;
                    }),
            }),
            {
                name: 'auth-storage',
            }
        ),
        { name: 'auth-store' }
    )
);

// Register reset
registerReset(() => useAuthStore.setState(initialState));